import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaExternalLinkAlt,
  FaHandshake,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { FiArrowRight, FiGlobe } from "react-icons/fi";

import { getAllPartenaires } from "../../services/partenairesServices";
import useScrollToTop from "../../hooks/useScollToTop";

const FALLBACK = "/img/no-image.jpg";
const API_BASE = process.env.REACT_APP_API_URL || "";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isActive(value) {
  if (value === undefined || value === null || value === "") return true;
  return value === true || value === 1 || value === "1";
}

function toAbsolute(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/img/")) return cleanUrl;

  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  if (cleanUrl.startsWith("/")) return cleanUrl;

  return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
}

function normalize(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function PartnersSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={index}
          className="h-72 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function EmptyState({ hasSearch }) {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaHandshake />
      </div>

      <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        {hasSearch ? "Aucun partenaire trouvé" : "Aucun partenaire publié"}
      </h2>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        {hasSearch
          ? "Essayez avec un autre mot-clé ou effacez la recherche."
          : "Les partenaires du club apparaîtront ici dès qu’ils seront ajoutés dans l’administration."}
      </p>
    </div>
  );
}

function PartnerCard({ partenaire }) {
  const logo = toAbsolute(partenaire?.photo_url) || FALLBACK;
  const hasDetail = Boolean(partenaire?.is_detail);
  const hasLink = Boolean(partenaire?.link_url);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className="relative flex h-48 items-center justify-center overflow-hidden bg-slate-50 p-6 dark:bg-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.16),transparent_36%)] opacity-0 transition group-hover:opacity-100" />

        <img
          src={logo}
          alt={partenaire?.nom || "Partenaire"}
          loading="lazy"
          decoding="async"
          className="relative z-10 max-h-28 w-full object-contain transition duration-500 group-hover:scale-105"
          onError={(event) => {
            event.currentTarget.src = FALLBACK;
          }}
        />

        {(hasDetail || hasLink) && (
          <div className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-800 shadow-xl backdrop-blur-xl transition group-hover:bg-primary group-hover:text-white dark:border-white/10 dark:bg-slate-950/85 dark:text-slate-100">
            {hasDetail ? <FaHandshake /> : <FaExternalLinkAlt />}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h2 className="line-clamp-2 text-center font-barlow text-xl font-bold leading-tight text-slate-950 dark:text-white">
          {partenaire?.nom || "Partenaire"}
        </h2>

        <p className="mx-auto mt-3 inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
          {hasDetail ? "Fiche détaillée" : hasLink ? "Site partenaire" : "Partenaire"}
        </p>

        <div className="mt-auto flex flex-col gap-3 pt-6">
          {hasDetail && (
            <Link
              to={`/partenaires/${encodeURIComponent(partenaire.nom)}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
            >
              Voir la fiche
              <FiArrowRight />
            </Link>
          )}

          {hasLink && (
            <a
              href={partenaire.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition",
                hasDetail
                  ? "border border-slate-200 bg-white text-slate-700 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                  : "bg-slate-950 text-white shadow-lg hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              )}
            >
              Ouvrir le site
              <FaExternalLinkAlt className="text-xs" />
            </a>
          )}

          {!hasDetail && !hasLink && (
            <p className="text-center text-sm italic text-slate-500 dark:text-slate-400">
              Coordonnées non renseignées.
            </p>
          )}
        </div>
      </div>

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90 transition group-hover:opacity-100"
      />
    </article>
  );
}

export default function TousLesPartenaires() {
  useScrollToTop();

  const navigate = useNavigate();

  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchPartenaires = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const all = await getAllPartenaires();

        const active = (Array.isArray(all) ? all : [])
          .filter((partenaire) => isActive(partenaire?.is_active))
          .sort((a, b) => {
            const orderA = Number(a?.ordre ?? a?.position ?? 0);
            const orderB = Number(b?.ordre ?? b?.position ?? 0);
            return orderA - orderB || String(a?.nom || "").localeCompare(String(b?.nom || ""));
          });

        if (!mounted) return;

        setPartenaires(active);
      } catch (error) {
        console.error("Erreur chargement partenaires :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les partenaires.");
          setPartenaires([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPartenaires();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredPartenaires = useMemo(() => {
    const query = normalize(search);

    if (!query) return partenaires;

    return partenaires.filter((partenaire) => {
      const haystack = normalize(
        `${partenaire?.nom || ""} ${partenaire?.adresse || ""} ${partenaire?.link_url || ""}`
      );

      return haystack.includes(query);
    });
  }, [partenaires, search]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaHandshake />
            Réseau du club
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Tous nos{" "}
            <span className="text-primary">partenaires</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Découvrez les structures, entreprises et soutiens qui accompagnent le
            club dans son développement.
          </p>
        </section>

        <section className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3">
          <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaHandshake className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {partenaires.length}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Partenaires
            </p>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaExternalLinkAlt className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {partenaires.filter((p) => p?.link_url).length}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Sites web
            </p>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FiGlobe className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {partenaires.filter((p) => p?.is_detail).length}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Fiches
            </p>
          </div>
        </section>

        <section className="mt-10 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-7">
          <div className="mb-7 rounded-[2rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5 sm:p-5">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher un partenaire..."
                className="h-14 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-12 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:placeholder:text-slate-500"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary dark:hover:bg-white/10"
                  aria-label="Effacer la recherche"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {loading && <PartnersSkeleton />}

          {!loading && errorMsg && (
            <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
              {errorMsg}
            </div>
          )}

          {!loading && !errorMsg && filteredPartenaires.length === 0 && (
            <EmptyState hasSearch={Boolean(search)} />
          )}

          {!loading && !errorMsg && filteredPartenaires.length > 0 && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredPartenaires.map((partenaire) => (
                <PartnerCard key={partenaire.id} partenaire={partenaire} />
              ))}
            </div>
          )}

          <div
            aria-hidden
            className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
          />
        </section>
      </div>
    </main>
  );
}